const CONSUMER_ID_PATTERN = /^(shell\.[a-z0-9]+(?:[.-][a-z0-9]+)*|plugin\.[a-z0-9]+(?:\.[a-z0-9]+)+)$/;

const THEME_SCOPE_DEFINITIONS = Object.freeze([
  {
    scopeId: 'settings-only',
    label: 'Settings only',
    description: 'Apply a theme mode only to the shared Settings plugin.',
    priority: 100
  },
  {
    scopeId: 'all-theme-enabled-plugins',
    label: 'All theme-enabled plugins',
    description: 'Apply a theme mode to every theme-capable plugin surface.',
    priority: 50
  },
  {
    scopeId: 'full-app',
    label: 'Full app',
    description: 'Apply a theme mode to the shell and all theme-capable plugins.',
    priority: 10
  }
]);

const consumersById = new Map();

let themeCapabilityFlags = {
  fullAppReady: false
};

function normalizeConsumerId(value) {
  return String(value || '').trim().toLowerCase();
}

function cloneConsumerDefinition(definition) {
  if (!definition) return null;
  return { ...definition };
}

function normalizeConsumerDefinition(definition = {}) {
  const consumerId = normalizeConsumerId(definition.consumerId);
  if (!CONSUMER_ID_PATTERN.test(consumerId)) {
    throw new Error(`Invalid theme consumer id "${consumerId}".`);
  }
  if (typeof definition.applyThemeMode !== 'function') {
    throw new Error(`Theme consumer "${consumerId}" is missing applyThemeMode().`);
  }
  return {
    consumerId,
    label: String(definition.label || consumerId).trim() || consumerId,
    kind: definition.kind === 'shell' ? 'shell' : 'plugin',
    scopeGroup: String(definition.scopeGroup || '').trim(),
    supportsThemeModes: definition.supportsThemeModes === true,
    applyThemeMode: definition.applyThemeMode,
    clearThemePreview: typeof definition.clearThemePreview === 'function'
      ? definition.clearThemePreview
      : null,
    priority: Number.isFinite(definition.priority) ? Number(definition.priority) : 0,
    globalThemeReady: definition.globalThemeReady === true,
    rootElRef: definition.rootElRef || null
  };
}

function compareByPriorityThenId(a, b) {
  if (b.priority !== a.priority) return b.priority - a.priority;
  return a.consumerId.localeCompare(b.consumerId, 'en', { sensitivity: 'base' });
}

function getRegisteredConsumers() {
  return Array.from(consumersById.values());
}

function getThemeCapablePluginConsumers() {
  return getRegisteredConsumers().filter((consumer) => (
    consumer.kind === 'plugin' && consumer.supportsThemeModes === true
  ));
}

function getThemeCapableShellConsumers() {
  return getRegisteredConsumers().filter((consumer) => (
    consumer.kind === 'shell' && consumer.supportsThemeModes === true
  ));
}

function getScopedEligibleConsumers(scopeId = '') {
  const normalizedScopeId = String(scopeId || '').trim();
  const themeCapable = getRegisteredConsumers().filter((consumer) => consumer.supportsThemeModes === true);

  if (normalizedScopeId === 'settings-only') {
    return themeCapable.filter((consumer) => consumer.consumerId === 'plugin.platform.settings');
  }
  if (normalizedScopeId === 'all-theme-enabled-plugins') {
    return themeCapable.filter((consumer) => consumer.kind === 'plugin');
  }
  if (normalizedScopeId === 'full-app') {
    return themeCapable.filter((consumer) => consumer.kind === 'plugin' || consumer.kind === 'shell');
  }
  return [];
}

export function registerThemeConsumer(definition = {}) {
  const normalizedDefinition = normalizeConsumerDefinition(definition);
  consumersById.set(normalizedDefinition.consumerId, normalizedDefinition);
  return cloneConsumerDefinition(normalizedDefinition);
}

export function unregisterThemeConsumer(consumerId) {
  const normalizedConsumerId = normalizeConsumerId(consumerId);
  consumersById.delete(normalizedConsumerId);
}

export function listThemeConsumers() {
  return getRegisteredConsumers()
    .slice()
    .sort((a, b) => a.consumerId.localeCompare(b.consumerId, 'en', { sensitivity: 'base' }))
    .map((definition) => cloneConsumerDefinition(definition));
}

export function getThemeConsumer(consumerId) {
  const normalizedConsumerId = normalizeConsumerId(consumerId);
  return cloneConsumerDefinition(consumersById.get(normalizedConsumerId));
}

export function resolveThemeScope(scopeId) {
  const normalizedScopeId = String(scopeId || '').trim();
  const definition = THEME_SCOPE_DEFINITIONS.find((item) => item.scopeId === normalizedScopeId);
  return definition ? { ...definition } : null;
}

export function canApplyThemeScope(scopeId) {
  const normalizedScopeId = String(scopeId || '').trim();
  if (normalizedScopeId === 'settings-only') {
    return consumersById.has('plugin.platform.settings');
  }
  if (normalizedScopeId === 'all-theme-enabled-plugins') {
    return getThemeCapablePluginConsumers().length >= 2;
  }
  if (normalizedScopeId === 'full-app') {
    return themeCapabilityFlags.fullAppReady === true
      && getThemeCapableShellConsumers().length >= 1
      && getThemeCapablePluginConsumers().length >= 1;
  }
  return false;
}

export function listThemeScopes() {
  return THEME_SCOPE_DEFINITIONS
    .filter((definition) => canApplyThemeScope(definition.scopeId))
    .slice()
    .sort((a, b) => {
      if (b.priority !== a.priority) return b.priority - a.priority;
      return a.scopeId.localeCompare(b.scopeId, 'en', { sensitivity: 'base' });
    })
    .map((definition) => ({ ...definition }));
}

export function applyThemeScope(scopeId, themeId, options = {}) {
  const normalizedScopeId = String(scopeId || '').trim();
  const mode = options && options.mode === 'preview' ? 'preview' : 'apply';
  if (!canApplyThemeScope(normalizedScopeId)) {
    return {
      applied: [],
      failed: []
    };
  }

  const eligibleConsumers = getScopedEligibleConsumers(normalizedScopeId)
    .slice()
    .sort(compareByPriorityThenId);

  const result = {
    applied: [],
    failed: []
  };

  eligibleConsumers.forEach((consumer) => {
    try {
      consumer.applyThemeMode(themeId, { mode });
      result.applied.push(consumer.consumerId);
    } catch (error) {
      result.failed.push({
        id: consumer.consumerId,
        errorMessage: String(error && error.message || error || 'Failed to apply theme scope.')
      });
    }
  });

  return result;
}

export function setThemeCapabilityFlags(flags = {}) {
  const nextFlags = flags && typeof flags === 'object' ? flags : {};
  themeCapabilityFlags = {
    fullAppReady: nextFlags.fullAppReady === true
  };
  return getThemeCapabilityFlags();
}

export function getThemeCapabilityFlags() {
  return { ...themeCapabilityFlags };
}
